import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

/**
 * Safe Area Insets System
 *
 * Inspired by iOS's safe area API and kbar's useRegisterActions pattern.
 * Components can register their dimensions to contribute to the safe area,
 * and the SafeArea component consumes these values to calculate its bounds.
 *
 * Data structure optimized for fast lookups:
 * - Map<id, contribution> for O(1) register/update/unregister
 * - Grouped by edge for O(1) inset computation per edge
 */

export type SafeAreaInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type SafeAreaEdge = keyof SafeAreaInsets;

export type MaxDimensions = {
  maxWidth: number | undefined;
  maxHeight: number | undefined;
};

type InsetContribution = {
  value: number;
  active: boolean;
};

// Contributions grouped by edge, then by id for O(1) lookups
type ContributionsByEdge = Record<SafeAreaEdge, Map<string, InsetContribution>>;

type SafeAreaInsetsStore = {
  contributions: ContributionsByEdge;
  subscribe: (callback: () => void) => () => void;
  getInsets: () => SafeAreaInsets;
  getMaxDimensions: () => MaxDimensions;
  setMaxDimensions: (dims: Partial<MaxDimensions>) => void;
  register: (
    id: string,
    edge: SafeAreaEdge,
    value: number,
    active: boolean
  ) => void;
  update: (
    id: string,
    edge: SafeAreaEdge,
    value: number,
    active: boolean
  ) => void;
  unregister: (id: string, edge: SafeAreaEdge) => void;
};

const createContributionsByEdge = (): ContributionsByEdge => ({
  top: new Map(),
  right: new Map(),
  bottom: new Map(),
  left: new Map(),
});

const computeEdgeInset = (edgeMap: Map<string, InsetContribution>): number => {
  return [...edgeMap.values()].reduce((max, contribution) => {
    if (contribution.active && contribution.value > max) {
      return contribution.value;
    }
    return max;
  }, 0);
};

const createStore = (): SafeAreaInsetsStore => {
  const contributions = createContributionsByEdge();
  const listeners = new Set<() => void>();
  let maxDimensions: MaxDimensions = {
    maxWidth: undefined,
    maxHeight: undefined,
  };

  const notify = () => {
    listeners.forEach(listener => listener());
  };

  const getInsets = (): SafeAreaInsets => ({
    top: computeEdgeInset(contributions.top),
    right: computeEdgeInset(contributions.right),
    bottom: computeEdgeInset(contributions.bottom),
    left: computeEdgeInset(contributions.left),
  });

  const getMaxDimensions = (): MaxDimensions => maxDimensions;

  const setMaxDimensions = (dims: Partial<MaxDimensions>) => {
    // Use 'in' to check if key exists, allowing explicit undefined values
    const newMaxWidth =
      'maxWidth' in dims ? dims.maxWidth : maxDimensions.maxWidth;
    const newMaxHeight =
      'maxHeight' in dims ? dims.maxHeight : maxDimensions.maxHeight;

    if (
      newMaxWidth === maxDimensions.maxWidth &&
      newMaxHeight === maxDimensions.maxHeight
    ) {
      return; // No change
    }

    maxDimensions = {
      maxWidth: newMaxWidth,
      maxHeight: newMaxHeight,
    };
    notify();
  };

  return {
    contributions,
    subscribe: (callback: () => void) => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    getInsets,
    getMaxDimensions,
    setMaxDimensions,
    register: (
      id: string,
      edge: SafeAreaEdge,
      value: number,
      active: boolean
    ) => {
      contributions[edge].set(id, { value, active });
      notify();
    },
    update: (
      id: string,
      edge: SafeAreaEdge,
      value: number,
      active: boolean
    ) => {
      const existing = contributions[edge].get(id);
      if (existing && existing.value === value && existing.active === active) {
        return; // No change
      }
      contributions[edge].set(id, { value, active });
      notify();
    },
    unregister: (id: string, edge: SafeAreaEdge) => {
      contributions[edge].delete(id);
      notify();
    },
  };
};

const SafeAreaInsetsContext = createContext<SafeAreaInsetsStore | null>(null);

export type SafeAreaInsetsProviderProps = {
  children: ReactNode;
};

/**
 * Provider that holds all safe area inset contributions.
 * Wrap your app/builder with this to enable the safe area insets system.
 */
export const SafeAreaInsetsProvider: React.FC<SafeAreaInsetsProviderProps> = ({
  children,
}) => {
  const storeRef = useRef<SafeAreaInsetsStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createStore();
  }

  return (
    <SafeAreaInsetsContext.Provider value={storeRef.current}>
      {children}
    </SafeAreaInsetsContext.Provider>
  );
};

/**
 * Hook to access the store (internal use).
 */
export const useSafeAreaInsetsStore = () => {
  const store = useContext(SafeAreaInsetsContext);
  if (!store) {
    throw new Error(
      'useSafeAreaInsetsStore must be used within a SafeAreaInsetsProvider'
    );
  }
  return store;
};

export type UseRegisterSafeAreaInsetOptions = {
  /** Unique identifier for this inset contribution */
  id: string;
  /** Which edge this inset applies to */
  edge: SafeAreaEdge;
  /** The inset value in pixels */
  value: number;
  /** Whether this inset is currently active (defaults to true) */
  active?: boolean;
};

/**
 * Hook for components to register their safe area inset contribution.
 *
 * @example
 * // Always active component
 * useRegisterSafeAreaInset({ id: 'top-bar', edge: 'top', value: 60 });
 *
 * @example
 * // Toggleable component
 * useRegisterSafeAreaInset({ id: 'step-settings', edge: 'right', value: 320, active: isOpen });
 */
export const useRegisterSafeAreaInset = ({
  id,
  edge,
  value,
  active = true,
}: UseRegisterSafeAreaInsetOptions): void => {
  const store = useSafeAreaInsetsStore();

  // Register on mount, unregister on unmount
  useEffect(() => {
    store.register(id, edge, value, active);
    return () => store.unregister(id, edge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, edge]);

  // Update when value or active changes
  useEffect(() => {
    store.update(id, edge, value, active);
  }, [store, id, edge, value, active]);
};

/**
 * Hook to consume the aggregated safe area insets.
 * Use this in the SafeArea component to get the computed insets.
 *
 * @example
 * const insets = useSafeAreaInsets();
 * // { top: 60, right: 0, bottom: 66, left: 0 }
 */
export const useSafeAreaInsets = (): SafeAreaInsets => {
  const store = useSafeAreaInsetsStore();
  const [insets, setInsets] = useState(() => store.getInsets());

  useEffect(() => {
    setInsets(store.getInsets());

    return store.subscribe(() => {
      setInsets(store.getInsets());
    });
  }, [store]);

  return insets;
};

/**
 * Hook to consume the max dimensions.
 *
 * @example
 * const { maxWidth, maxHeight } = useSafeAreaMaxDimensions();
 */
export const useSafeAreaMaxDimensions = (): MaxDimensions => {
  const store = useSafeAreaInsetsStore();
  const [dims, setDims] = useState(() => store.getMaxDimensions());

  useEffect(() => {
    setDims(store.getMaxDimensions());

    return store.subscribe(() => {
      setDims(store.getMaxDimensions());
    });
  }, [store]);

  return dims;
};

/**
 * Optional hook to check if within provider (for conditional usage).
 * Returns both insets and maxDimensions.
 */
export const useSafeAreaInsetsOptional = ():
  | (SafeAreaInsets & MaxDimensions)
  | null => {
  const store = useContext(SafeAreaInsetsContext);
  const [state, setState] = useState<(SafeAreaInsets & MaxDimensions) | null>(
    () => (store ? { ...store.getInsets(), ...store.getMaxDimensions() } : null)
  );

  useEffect(() => {
    if (!store) {
      return undefined;
    }

    setState({ ...store.getInsets(), ...store.getMaxDimensions() });

    return store.subscribe(() => {
      setState({ ...store.getInsets(), ...store.getMaxDimensions() });
    });
  }, [store]);

  return state;
};
